import React, { useEffect, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { respondToAuthChallenge, resendAuthChallengeCode } from '#action/auth/auth-challenge';
import { whitelabel } from '#config/whitelabel';
import {
  StyledMain,
  DivPaper,
  MainBg,
  CompanyImgCnt,
  CompanyImgLogo,
  StyledH5,
  StyledForm,
  ButtonsContainer
} from './styles';
import validate from './validate';
import { requestStatuses as statuses } from '#helper';

const CODE_RESEND_INTERVAL_SECONDS = 60;

const AuthChallenge = props => {
  const { socket } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const { challenge, resendCodeStatus } = useSelector(state => state.authChallenge);

  const [resendCodeRemainingSeconds, setResendCodeRemainingSeconds] = useState(0);

  const { control, handleSubmit, formState: { isSubmitting, isDirty } } = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: { code: '' }
  });

  useEffect(() => {
    if (challenge?.challengeIssuedAt) {
      const issuedAt = new Date(challenge.challengeIssuedAt);
      const now = new Date();
      const diff = now - issuedAt;
      const remainingSeconds = CODE_RESEND_INTERVAL_SECONDS - Math.floor(diff / 1000);
      setResendCodeRemainingSeconds(remainingSeconds > 0 ? remainingSeconds : 0);
    }
  }, [challenge?.challengeIssuedAt, setResendCodeRemainingSeconds]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (resendCodeRemainingSeconds > 0) {
        setResendCodeRemainingSeconds(resendCodeRemainingSeconds - 1);
      }
    }, 1000);

    if (resendCodeRemainingSeconds === 0) {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [resendCodeRemainingSeconds, setResendCodeRemainingSeconds]);

  useEffect(() => {
    if (!challenge) {
      history.push('/login');
    }
  }, [challenge, history]);

  const handleFormSubmit = async data => {
    await dispatch(respondToAuthChallenge({
      challengeName: challenge.challengeName,
      sessionId: challenge.sessionId,
      challengeResponse: {
        code: Number(data.code?.trim())
      }
    }, socket));
  };

  const handleResendCode = async () => {
    await dispatch(resendAuthChallengeCode({
      challengeName: challenge.challengeName,
      sessionId: challenge.sessionId,
      challengeResponse: {
        resendCode: true
      }
    }));
  };

  return (
    <>
      <MainBg />
      <StyledMain>
        <CssBaseline />
        <DivPaper>
          <CompanyImgCnt>
            <CompanyImgLogo
              src={whitelabel.buildAssetUrl('media/logo.svg')}
              alt="logo"
            />
          </CompanyImgCnt>
          <StyledH5 component="h1" variant="h5">
            Please enter the code sent to your email
          </StyledH5>
          <StyledForm
            onSubmit={handleSubmit(handleFormSubmit)}
            autoComplete="off"
          >
            <RHFTextField
              name="code"
              control={control}
              label="Code"
              variant="outlined"
              fullWidth
              margin="normal"
            />
            <ButtonsContainer>
              <Button
                disabled={!isDirty || isSubmitting}
                type="submit"
                variant="contained"
                color="secondary"
              >
                Sign in
              </Button>
              <Button
                disabled={resendCodeStatus === statuses.loading || resendCodeRemainingSeconds > 0}
                onClick={handleResendCode}
              >
                Resend code
                {!!resendCodeRemainingSeconds && (`(${resendCodeRemainingSeconds})`)}
              </Button>
            </ButtonsContainer>
          </StyledForm>
        </DivPaper>
      </StyledMain>
    </>
  );
};

export default AuthChallenge;
